/**
 * Advocate Blog v1 — types.
 *
 * Mirrors the backend `advocate_blog` router + `list_my_posts` SQL shape.
 * `content_json` can hold either shape described in `src/lib/blog/content.ts`
 * (legacy BlockNote block array, or the new `{format: 'markdown', markdown}`
 * object) — use that module's helpers to discriminate rather than
 * `Array.isArray` checks scattered across callers.
 */

import type { BlogContentJson } from '@/lib/blog/content';

export type BlogPostStatus = 'draft' | 'published' | 'unpublished' | 'deleted';

export type BlogPostLocale = 'sk' | 'en';

export interface BlogPost {
  id: string;
  professional_id: string;
  slug: string;
  locale: BlogPostLocale;
  title: string;
  meta_description: string | null;
  cover_image_path: string | null;
  /** See `src/lib/blog/content.ts` for the two shapes this can hold. */
  content_json: BlogContentJson;
  status: BlogPostStatus;
  view_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogQuota {
  used: number;
  cap: number | null;
  unlimited: boolean;
  plan_tag: string;
}

/** Response of `GET /api/blog/me/enabled` — the global advocate_blog flag. */
export interface BlogEnabled {
  enabled: boolean;
}

export interface CreatePostRequest {
  professional_id: string;
  slug: string;
  locale: BlogPostLocale;
  title: string;
}

// Partial-update payload: an absent / `undefined` field means "leave unchanged"
// (callers build it from optional form state), so under exactOptionalPropertyTypes
// the optional fields explicitly admit `undefined`.
export interface UpdatePostRequest {
  title?: string | undefined;
  meta_description?: string | null | undefined;
  content_json?: BlogContentJson | undefined;
  slug?: string | undefined;
}

/** Backend list response: `{items, next_offset}` — `next_offset === null` means "no more". */
export interface BlogPostListResponse {
  items: BlogPost[];
  next_offset: number | null;
}

/** Server-emitted error codes. Add new codes here when the backend grows new ones. */
export type BlogErrorCode =
  | 'blog_not_claimed'
  | 'blog_quota_exceeded'
  | 'blog_slug_collision'
  | 'blog_invalid_slug'
  | 'blog_invalid_locale'
  | 'blog_invalid_pagination'
  | 'blog_not_found'
  | 'blog_invalid_state'
  | 'blog_empty_content'
  /** #6834: this tab's dialog rendered a rule set the server has superseded. */
  | 'blog_rules_version_stale';

/**
 * Raw error envelope FastAPI emits from HTTPException. Mirrors
 * `detail={code, message?, ...extras}` in `advocate_blog.py`.
 */
export interface BlogError {
  code: BlogErrorCode;
  message?: string;
  /** Quota errors carry current usage and cap. */
  used?: number;
  cap?: number | null;
  plan_tag?: string;
  /** Slug-collision errors carry the rejected slug. */
  slug?: string;
  /** `blog_rules_version_stale` carries the version the server expects (#6834). */
  server_rules_version?: string;
}

/**
 * Typed exception thrown by `BlogService` on API errors. Lets callers branch
 * on `err.code` (e.g. show a quota upgrade CTA for `blog_quota_exceeded`)
 * without string-matching free-form message text.
 */
export class BlogApiError extends Error {
  public readonly code: BlogErrorCode;
  public readonly used?: number;
  public readonly cap?: number | null;
  public readonly slug?: string;
  /** `blog_rules_version_stale` carries the version the server expects (#6834). */
  public readonly server_rules_version?: string;

  constructor(
    code: BlogErrorCode,
    message?: string,
    extras?: Partial<BlogError>
  ) {
    super(message ?? code);
    this.name = 'BlogApiError';
    this.code = code;
    if (extras?.used !== undefined) this.used = extras.used;
    if (extras?.cap !== undefined) this.cap = extras.cap;
    if (extras?.slug !== undefined) this.slug = extras.slug;
    if (extras?.server_rules_version !== undefined)
      this.server_rules_version = extras.server_rules_version;
  }
}
